import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Plus, Trash2, Star, Hash } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { inventoryService } from '@/services/inventory';
import type { ItemIdentifier, ItemIdentifierKind } from '@/types/inventory';

interface Props {
  itemId: string | null;
}

const KINDS: { value: ItemIdentifierKind; label: string; placeholder: string }[] = [
  { value: 'UPC',          label: 'UPC',           placeholder: '012345678905' },
  { value: 'EAN',          label: 'EAN',           placeholder: '5901234123457' },
  { value: 'ISBN',         label: 'ISBN',          placeholder: '978-3-16-148410-0' },
  { value: 'ASIN',         label: 'ASIN',          placeholder: 'B07PXGQC1Q' },
  { value: 'MODEL_NUMBER', label: 'Model #',       placeholder: 'XR-2024-PRO' },
  { value: 'PART_NUMBER',  label: 'Part #',        placeholder: 'PN-19452' },
  { value: 'MFR_PART',     label: 'Mfr Part #',    placeholder: 'ACM-01-RED' },
  { value: 'OTHER',        label: 'Other',         placeholder: 'Custom code' },
];

const KIND_LABEL: Record<ItemIdentifierKind, string> = Object.fromEntries(
  KINDS.map((k) => [k.value, k.label]),
) as Record<ItemIdentifierKind, string>;

/**
 * Inline editor for an item's secondary identifiers (UPC / EAN / ISBN / etc.).
 * Lives on the InventoryDetailSheet — not on the form, since it's a list
 * managed independently of the catalog metadata.
 */
export function ItemIdentifiersSection({ itemId }: Props) {
  const qc = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [newKind, setNewKind] = useState<ItemIdentifierKind>('UPC');
  const [newValue, setNewValue] = useState('');
  const [newPrimary, setNewPrimary] = useState(false);

  const query = useQuery({
    queryKey: ['inventory', 'identifiers', itemId],
    queryFn: () => inventoryService.listIdentifiers(itemId!),
    enabled: !!itemId,
  });

  const create = useMutation({
    mutationFn: () =>
      inventoryService.createIdentifier(itemId!, {
        kind: newKind,
        value: newValue.trim(),
        isPrimary: newPrimary,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['inventory', 'identifiers', itemId] });
      toast.success('Identifier added');
      setNewValue('');
      setNewPrimary(false);
      setAdding(false);
    },
  });

  const setPrimary = useMutation({
    mutationFn: (id: string) =>
      inventoryService.updateIdentifier(itemId!, id, { isPrimary: true }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['inventory', 'identifiers', itemId] }),
  });

  const remove = useMutation({
    mutationFn: (id: string) => inventoryService.deleteIdentifier(itemId!, id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['inventory', 'identifiers', itemId] });
      toast.success('Identifier removed');
    },
  });

  if (!itemId) return null;
  const items = query.data ?? [];

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="text-[10.5px] font-semibold uppercase tracking-[0.1em] text-muted-foreground">
          Identifiers
        </div>
        {!adding && (
          <Button size="sm" variant="outline" className="h-7" onClick={() => setAdding(true)}>
            <Plus className="h-3 w-3" />
            Add
          </Button>
        )}
      </div>

      {query.isLoading ? (
        <div className="text-[12px] text-muted-foreground">Loading…</div>
      ) : items.length === 0 && !adding ? (
        <div className="rounded-md border border-dashed border-border/70 bg-muted/20 px-3 py-4 text-center">
          <Hash className="mx-auto h-4 w-4 text-muted-foreground/60" />
          <div className="mt-1.5 text-[12px] text-muted-foreground">
            No identifiers yet. Add UPC, EAN, ISBN, model #, etc.
          </div>
        </div>
      ) : (
        <div className="overflow-hidden rounded-md border border-border/70">
          {items.map((it: ItemIdentifier) => (
            <div
              key={it.id}
              className="flex items-center justify-between gap-3 border-b border-border/60 px-3 py-2 last:border-0"
            >
              <div className="flex min-w-0 items-center gap-2.5">
                <Badge variant="muted" className="font-mono text-[10px]">
                  {KIND_LABEL[it.kind]}
                </Badge>
                <span className="font-mono text-[12.5px] text-foreground">{it.value}</span>
                {it.isPrimary && (
                  <Badge variant="success" className="text-[10px]">
                    <Star className="h-2.5 w-2.5" /> primary
                  </Badge>
                )}
              </div>
              <div className="flex items-center gap-0.5">
                {!it.isPrimary && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-7 w-7 text-muted-foreground hover:text-foreground"
                        onClick={() => setPrimary.mutate(it.id)}
                        disabled={setPrimary.isPending}
                      >
                        <Star className="h-3.5 w-3.5" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Set as primary</TooltipContent>
                  </Tooltip>
                )}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-7 w-7 text-muted-foreground hover:text-destructive"
                      onClick={() => remove.mutate(it.id)}
                      disabled={remove.isPending}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Remove</TooltipContent>
                </Tooltip>
              </div>
            </div>
          ))}
        </div>
      )}

      {adding && (
        <div className="space-y-2 rounded-md border border-border/70 bg-card p-3">
          <div className="grid grid-cols-3 gap-2">
            <Select value={newKind} onValueChange={(v) => setNewKind(v as ItemIdentifierKind)}>
              <SelectTrigger className="h-8 text-[12.5px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {KINDS.map((k) => (
                  <SelectItem key={k.value} value={k.value}>
                    {k.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="col-span-2">
              <Input
                value={newValue}
                onChange={(e) => setNewValue(e.target.value)}
                placeholder={KINDS.find((k) => k.value === newKind)?.placeholder ?? ''}
                className="h-8 font-mono text-[12.5px]"
                autoFocus
              />
            </div>
          </div>
          <label className="flex items-center gap-2 text-[12px] text-foreground">
            <input
              type="checkbox"
              checked={newPrimary}
              onChange={(e) => setNewPrimary(e.target.checked)}
              className="h-3.5 w-3.5 rounded border-input accent-primary"
            />
            Mark as primary
          </label>
          <div className="flex justify-end gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                setAdding(false);
                setNewValue('');
                setNewPrimary(false);
              }}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={() => create.mutate()}
              disabled={!newValue.trim() || create.isPending}
            >
              {create.isPending ? 'Adding…' : 'Add identifier'}
            </Button>
          </div>
          {create.error && (
            <div className="text-[11.5px] text-destructive">
              {create.error instanceof Error ? create.error.message : 'Could not add'}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
